import type { ReactNode } from 'react'
import type { FilterConfig, FilterState } from './FilterConfig'

import React, { FormEvent, useCallback, useState, useMemo, useEffect } from 'react'
import { Form, Button, Checkbox } from 'rsuite'
import LegacySearchIcon from '@rsuite/icons/legacy/Search'
import useSearchParams from '@@partner:helpers/hooks/useSearchParams'
import { FilterRenderer, parseUrlParamsFromConfig } from './FilterConfig'
import { parseMultilineToArray } from './FilterMultilineInput'
import './FilterPanel.scss'

// ===== ТИПЫ =====

export interface FilterPanelProps<T extends FilterState, TApi = T> {
  /** Конфигурация фильтров */
  filters: FilterConfig[]
  /** Начальное состояние */
  defaultState: T
  /** Callback при поиске */
  onSearch: (state: TApi) => void
  /** Преобразование URL параметров в state */
  parseUrlParams?: (params: Record<string, any>) => Partial<T>
  /** Преобразование state перед отправкой в API */
  transformBeforeSubmit?: (state: T) => TApi
  /** Синхронизировать с URL (по умолчанию true) */
  syncWithUrl?: boolean
  /** Автоматически вызвать onSearch при инициализации */
  searchOnInit?: boolean
  // Массовое обновление (опционально)
  showBulkUpdate?: boolean
  onBulkUpdate?: (show: boolean) => void
  // Дополнительные кнопки/контролы (рендерятся снизу панели)
  extraControls?: ReactNode
  // Дополнительные действия в submit-группе (рендерятся над кнопкой Поиск)
  submitGroupExtra?: ReactNode
  // Кастомный текст кнопки
  submitText?: string
  // Компактный режим
  compact?: boolean
}

export default function FilterPanel<T extends FilterState, TApi = T> ({
  filters,
  defaultState,
  onSearch,
  parseUrlParams,
  transformBeforeSubmit,
  syncWithUrl = true,
  searchOnInit = true,
  showBulkUpdate,
  onBulkUpdate,
  extraControls,
  submitGroupExtra,
  submitText = 'Поиск',
  compact = false
}: FilterPanelProps<T, TApi>) {
  const { getSearchParams, setSearchParams } = useSearchParams()
  
  const [ state, setState ] = useState<T>(defaultState)
  const [ isInitialized, setIsInitialized ] = useState(false)

  // Сырые значения для multiline полей
  const multilineKeys = useMemo(
    () => filters.filter(f => f.type === 'multiline').map(f => f.key),
    [ filters ]
  )

  const [ rawValues, setRawValues ] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    multilineKeys.forEach(key => {
      const value = defaultState[key]
      initial[key] = Array.isArray(value) ? value.join('\n') : ''
    })
    return initial
  })

  // Функция для обновления rawValues при изменении state
  const updateRawValues = useCallback((newState: T) => {
    multilineKeys.forEach(key => {
      const value = newState[key]
      setRawValues(prev => ({
        ...prev,
        [key]: Array.isArray(value) ? value.join('\n') : ''
      }))
    })
  }, [ multilineKeys ])

  // Инициализация из URL при маунте
  useEffect(() => {
    if (!syncWithUrl || isInitialized) return

    const urlParams = getSearchParams()
    
    // Используем переданный parseUrlParams или автоматический парсинг на основе конфигурации
    const parsedParams = parseUrlParams 
      ? parseUrlParams(urlParams) 
      : parseUrlParamsFromConfig(urlParams, filters, defaultState)

    // Мержим с дефолтным стейтом
    const mergedState = { ...defaultState }
    Object.keys(parsedParams).forEach(key => {
      if (parsedParams[key] !== undefined) {
        (mergedState as any)[key] = parsedParams[key]
      }
    })

    setState(mergedState)
    updateRawValues(mergedState)
    setIsInitialized(true)

    // Вызываем onSearch если нужно
    if (searchOnInit) {
      const apiState = transformBeforeSubmit 
        ? transformBeforeSubmit(mergedState) 
        : mergedState as unknown as TApi
      onSearch(apiState)
    }
  }, [ syncWithUrl, isInitialized ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleRawChange = useCallback((key: string, value: string) => {
    setRawValues(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleRawBlur = useCallback((key: string) => {
    const config = filters.find(f => f.key === key) as any
    const pattern = config?.linePattern
    const parsed = parseMultilineToArray(rawValues[key] || '', pattern)
    
    setState(prev => ({ ...prev, [key]: parsed as T[keyof T] }))
    setRawValues(prev => ({ ...prev, [key]: parsed.join('\n') }))
  }, [ filters, rawValues ])

  const handleSubmit = useCallback((_formValue: Record<string, any> | null, event?: FormEvent) => {
    event?.preventDefault()
    
    // Сохраняем в URL
    if (syncWithUrl) {
      setSearchParams(state)
    }
    
    // Преобразуем и отправляем
    const apiState = transformBeforeSubmit 
      ? transformBeforeSubmit(state) 
      : state as unknown as TApi
    onSearch(apiState)
  }, [ onSearch, state, syncWithUrl, setSearchParams, transformBeforeSubmit ])

  return (
    <Form 
      className={`FilterPanel ${compact ? 'FilterPanel--compact' : ''}`}
      onSubmit={handleSubmit}
    >
      {/* Не рендерим фильтры пока не завершится парсинг URL */}
      {(!syncWithUrl || isInitialized) && (
        <FilterRenderer
          filters={filters}
          state={state}
          onChange={handleChange}
          rawValues={rawValues}
          onRawChange={handleRawChange}
          onRawBlur={handleRawBlur}
          submitButton={
            <div className="FilterPanel__SubmitGroup">
              {submitGroupExtra}
              <Button type="submit" size="lg" color="green" appearance="primary">
                <LegacySearchIcon /> {submitText}
              </Button>
              {onBulkUpdate && (
                <Checkbox
                  checked={showBulkUpdate}
                  onChange={() => onBulkUpdate(!showBulkUpdate)}
                >
                  Массовое обновление
                </Checkbox>
              )}
            </div>
          }
          bottomControls={extraControls}
        />
      )}
    </Form>
  )
}

